import { useCallback, useEffect, useState } from 'react';

export interface PopupPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  transform?: string;
}

export interface UseDynamicPopupPositionOptions {
  triggerRef: React.RefObject<HTMLElement>;
  popupRef: React.RefObject<HTMLElement>;
  isVisible: boolean;
  preferredPosition?: 'left' | 'right' | 'top' | 'bottom';
  offset?: number;
  viewportPadding?: number;
}

export function useDynamicPopupPosition({
  triggerRef,
  popupRef,
  isVisible,
  preferredPosition = 'right',
  offset = 8,
  viewportPadding = 16
}: UseDynamicPopupPositionOptions) {
  const [position, setPosition] = useState<PopupPosition>({});
  const [isPositioned, setIsPositioned] = useState(false);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !popupRef.current || !isVisible) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    const newPosition: PopupPosition = {};

    // Calculate available space in each direction
    const spaceRight = viewport.width - triggerRect.right;
    const spaceLeft = triggerRect.left;
    const spaceTop = triggerRect.top;
    const spaceBottom = viewport.height - triggerRect.bottom;

    // Determine if popup fits in preferred position
    const fitsRight = spaceRight >= popupRect.width + viewportPadding;
    const fitsLeft = spaceLeft >= popupRect.width + viewportPadding;
    const fitsTop = spaceTop >= popupRect.height + viewportPadding;
    const fitsBottom = spaceBottom >= popupRect.height + viewportPadding;

    // Choose position based on preference and available space
    let finalPosition = preferredPosition;

    if (preferredPosition === 'right' && !fitsRight && fitsLeft) {
      finalPosition = 'left';
    }
    if (preferredPosition === 'left' && !fitsLeft && fitsRight) {
      finalPosition = 'right';
    }
    if (preferredPosition === 'top' && !fitsTop && fitsBottom) {
      finalPosition = 'bottom';
    }
    if (preferredPosition === 'bottom' && !fitsBottom && fitsTop) {
      finalPosition = 'top';
    }

    // Apply positioning based on final position
    switch (finalPosition) {
      case 'right':
        newPosition.left = triggerRect.right + offset;
        newPosition.top = triggerRect.top - offset; // Align with top and position slightly above
        break;
      case 'left':
        newPosition.right = viewport.width - triggerRect.left + offset;
        newPosition.top = triggerRect.top - offset; // Align with top and position slightly above
        break;
      case 'top':
        newPosition.bottom = viewport.height - triggerRect.top + offset;
        newPosition.left = triggerRect.left + (triggerRect.width - popupRect.width) / 2;
        break;
      case 'bottom':
        newPosition.top = triggerRect.bottom + offset;
        newPosition.left = triggerRect.left + (triggerRect.width - popupRect.width) / 2;
        break;
    }

    // Ensure popup stays within viewport bounds
    if (newPosition.left !== undefined) {
      if (newPosition.left < viewportPadding) {
        newPosition.left = viewportPadding;
      }
      if (newPosition.left + popupRect.width > viewport.width - viewportPadding) {
        newPosition.left = viewport.width - popupRect.width - viewportPadding;
      }
    }

    if (newPosition.right !== undefined) {
      if (newPosition.right < viewportPadding) {
        newPosition.right = viewportPadding;
      }
      if (newPosition.right + popupRect.width > viewport.width - viewportPadding) {
        newPosition.right = viewport.width - popupRect.width - viewportPadding;
      }
    }

    if (newPosition.top !== undefined) {
      if (newPosition.top < viewportPadding) {
        newPosition.top = viewportPadding;
      }
      if (newPosition.top + popupRect.height > viewport.height - viewportPadding) {
        newPosition.top = viewport.height - popupRect.height - viewportPadding;
      }
    }

    if (newPosition.bottom !== undefined) {
      if (newPosition.bottom < viewportPadding) {
        newPosition.bottom = viewportPadding;
      }
      if (newPosition.bottom + popupRect.height > viewport.height - viewportPadding) {
        newPosition.bottom = viewport.height - popupRect.height - viewportPadding;
      }
    }

    setPosition(newPosition);
    setIsPositioned(true);
  }, [triggerRef, popupRef, isVisible, preferredPosition, offset, viewportPadding]);

  // Calculate position when popup becomes visible
  useEffect(() => {
    if (isVisible) {
      // Small delay to ensure popup is rendered and has dimensions
      const timeoutId = setTimeout(calculatePosition, 0);
      return () => clearTimeout(timeoutId);
    }
    setIsPositioned(false);
  }, [isVisible, calculatePosition]);

  // Recalculate position on window resize
  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const handleResize = () => {
      calculatePosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isVisible, calculatePosition]);

  // Recalculate position on scroll
  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const handleScroll = () => {
      calculatePosition();
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isVisible, calculatePosition]);

  return {
    position,
    isPositioned,
    recalculatePosition: calculatePosition
  };
}
